import React, {useState} from 'react';
import {Tab, TabContent, Tabs} from "../Shared/Tabs";
import {formatViewDateLong} from "../../utils/dateFormat";
import {getClaimValue, hasRoleClaim} from "../../utils/authUser";
import LimeClaimTypes from "../../models/limeClaimTypes";
import {useAuth} from "react-oidc-context";
import {limeApiFetch, limeApiGet} from "../../utils/api";
import AlertCircle from "../../images/alert-circle.svg";
import {useNavigate} from "react-router-dom";
import WellKnownRoles from "../../models/wellKnownRoles";
import LoadingSpinner from "../Shared/LoadingSpinner/LoadingSpinner";
import { Invoice, LimePaymentMethod, Payment, PaymentMethodStatus, PaymentTypeId } from "./types";
import PaymentFailureBanner from "./PaymentFailureBanner";
import CardBrandLogo from "./CardBrandLogo";
import { LimeConfig } from "../../utils/limeConfig";
import {loadStripe, Stripe} from "@stripe/stripe-js";
import { getCurrentUserPermissions } from "../../services/permissionsService";


import downloadIcon from "../../images/download-dark.svg";
import {saveAs} from "file-saver";
import {
  portalCardSurface,
  portalPageSubtitle,
  portalPageTitle,
  portalTableBodyCell,
  portalTableBodyRow,
  portalTableCardScrollable,
  portalTableHeadCell,
} from "../../theme/portalLayout";

function Billing() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [tabsLoading, setTabsLoading] = useState<boolean>(true);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [paymentMethodStatus, setPaymentMethodStatus] = useState<PaymentMethodStatus>(PaymentMethodStatus.Loading);
  const [paymentMethod, setPaymentMethod] = useState<LimePaymentMethod>();
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [hasManagePaymentMethodsPermission, setHasManagePaymentMethodsPermission] = useState(false);
  const [activeTabKey, setActiveTabKey] = useState("1");
  
  React.useEffect(() => {
    (async () => {
      if (!hasRoleClaim(auth.user, WellKnownRoles.OrganisationBillingViewer) &&
        !hasRoleClaim(auth.user, WellKnownRoles.OrganisationBillingManager)) {
        navigate('/access-denied');
      }
      const config = await limeApiGet<LimeConfig>('configuration', auth);
      setStripe(await loadStripe(config.stripeApiKey));
      await refreshTabs();
      await refreshPaymentMethods();
      await refreshPermissions();
    })();
  }, []);

  const navigateToAddPayment = () => {
    navigate('/billing/payment', { state: {updatePaymentMethod: !!paymentMethod}});
  }

  async function refreshTabs() {
    setTabsLoading(true);
    await refreshData();
    setTabsLoading(false);
  }

  async function refreshData() {
    const orgUuid = getClaimValue(auth.user, LimeClaimTypes.OrganisationUuid);
    const invoices = await limeApiGet<Invoice[]>(`organisations/${orgUuid}/invoices`, auth);
    setInvoices(invoices);
    const payments = await limeApiGet<Payment[]>(`organisations/${orgUuid}/payments`, auth);
    setPayments(payments);
  }

  async function refreshPaymentMethods() {
    setPaymentMethodStatus(PaymentMethodStatus.Loading);
    let orgUuid = getClaimValue(auth.user, LimeClaimTypes.OrganisationUuid);
    let response = await limeApiFetch(`organisations/${orgUuid}/paymentMethods`, 'GET', auth);
    if (response.status === 200) {
      let data = await response.json();
      setPaymentMethod(data);
      setPaymentMethodStatus(PaymentMethodStatus.Active);
    } else {
      if (response.status === 404) {
        let data = await response.json();
        if (data.code === "no_payment_method") {
          setPaymentMethodStatus(PaymentMethodStatus.Required)
        } else {
          setPaymentMethodStatus(PaymentMethodStatus.Error);
        }
      } else {
        setPaymentMethodStatus(PaymentMethodStatus.Error);
      }
    }
  }

  async function refreshPermissions() {
    const permissions = await getCurrentUserPermissions(auth);
    const hasPermission = permissions.includes("microservice-organisation-billing.ManagePaymentMethods");
    setHasManagePaymentMethodsPermission(hasPermission);
  }

  async function downloadInvoice(invoiceId: number, invoiceRef: string) {
    setTabsLoading(true);
    let orgUuid = getClaimValue(auth.user, LimeClaimTypes.OrganisationUuid);
    let response = await limeApiFetch(`organisations/${orgUuid}/invoices/${invoiceId}/download`, 'GET', auth);
    if (response.status === 200) {
      let blob = await response.blob();
      saveAs(blob, invoiceRef);
    }

    setTabsLoading(false);
  }

  function padValue(value: number): string {
    return value < 10 ? "0" + value.toString() : value.toString();
  }

  return (
    <div>
      <PaymentFailureBanner stripe={stripe} onPaymentProcessed={refreshData} />
      <h1 className={`mb-sm ${portalPageTitle}`}>Billing</h1>
      <p className={`${portalPageSubtitle} mb-lg`}>Manage your organisations invoices, payments and payment method</p>
      <LoadingSpinner loading={tabsLoading}>
        <Tabs activeTabKey={activeTabKey} onTabSelect={setActiveTabKey}>
        <Tab tabKey="1" name="Invoices"/>
        <Tab tabKey="2" name="Payments"/>
        <TabContent associatedTabKey="1">
          <div className={portalTableCardScrollable}>
          <table className="w-full">
            <thead className="sticky top-0 z-[1]">
            <tr className="h-2xl">
              <th className={portalTableHeadCell}>Reference</th>
              <th className={portalTableHeadCell}>Date</th>
              <th className={portalTableHeadCell}>Services</th>
              <th className={portalTableHeadCell}>Amount</th>
              <th className={portalTableHeadCell}>Status</th>
              <th className={portalTableHeadCell}/>
            </tr>
            </thead>
            <tbody>
            {
              invoices && invoices.length > 0 &&
              invoices.map((inv, i) =>
                <tr key={i} className={portalTableBodyRow}>
                  <td className={portalTableBodyCell}>
                    {inv.ref}
                  </td>
                  <td className={portalTableBodyCell}>
                    {formatViewDateLong(inv.date)}
                  </td>
                  <td className={portalTableBodyCell}>
                    {inv.products.map(p => p.name.trim()).join(', ')}
                  </td>
                  <td className={portalTableBodyCell}>
                    {inv.total.toLocaleString('en-GB', {
                      style: 'currency',
                      currency: 'GBP'})}
                  </td>
                  <td className={portalTableBodyCell}>
                    {inv.statusName}
                  </td>
                  <td className={portalTableBodyCell}>
                    <img
                      src={downloadIcon}
                      onClick={() => downloadInvoice(inv.id, inv.ref)}
                      alt="Download invoice"
                      className="h-lg hover:cursor-pointer"
                    />
                  </td>
                </tr>)
            }
            </tbody>
          </table>
          </div>
        </TabContent>
        <TabContent associatedTabKey="2">
          <div className={portalTableCardScrollable}>
          <table className="w-full">
            <thead className="sticky top-0 z-[1]">
              <tr className="h-2xl">
                <th className={portalTableHeadCell}>Date</th>
                <th className={portalTableHeadCell}>Amount</th>
                <th className={portalTableHeadCell}>Payment Method</th>
                <th className={portalTableHeadCell}>Status</th>
                <th className={portalTableHeadCell}>Invoices</th>
              </tr>
            </thead>
            <tbody>
            {
              (!payments || payments.length == 0) &&
                <tr className={portalTableBodyRow}>
                    <td colSpan={5} className={`${portalTableBodyCell} font-medium`}>No payments have been made yet</td>
                </tr>
            }
            {
              payments && payments.length > 0 &&
              payments.map((p, i) =>
                <tr key={i} className={portalTableBodyRow}>
                  <td className={portalTableBodyCell}>
                    {formatViewDateLong(p.date)}
                  </td>
                  <td className={portalTableBodyCell}>
                    {p.amount.toLocaleString('en-GB', {
                      style: 'currency',
                      currency: 'GBP'})}
                  </td>
                  <td className={portalTableBodyCell}>
                    {p.paymentMethodBrand} / {p.paymentMethodLast4Digits}
                  </td>
                  <td className={portalTableBodyCell}>
                    {p.typeId == PaymentTypeId.Charge ? p.statusName : "Refunded"}
                  </td>
                  <td className={portalTableBodyCell}>
                    {p.invoices.map(i => i.ref).join(', ')}
                  </td>
                </tr>)
            }
            </tbody>
          </table>
          </div>
        </TabContent>
      </Tabs>
      </LoadingSpinner>
      <h3 className="mb-md mt-xl font-sans-co font-bold text-portal-teal text-h3">Payment method</h3>
      <LoadingSpinner loading={paymentMethodStatus === PaymentMethodStatus.Loading}>
        <div className={`w-full max-w-md p-xl text-portal-teal ${portalCardSurface}`}>
          {
            paymentMethodStatus === PaymentMethodStatus.Error &&
              <p className="font-bold text-error-red">Sorry an error occurred loading your payment method</p>
          }
          {
              paymentMethodStatus === PaymentMethodStatus.Required &&
              <>
                <div className={`flex ${hasManagePaymentMethodsPermission ? 'mb-lg' : ''}`}>
                  <img src={AlertCircle} className="h-lg" alt="alert"/>
                  <p className="ml-md font-bold">
                    {invoices && invoices.length > 0 ?
                        (hasManagePaymentMethodsPermission ? "Please setup a payment method" : "Payment method required") :
                        "Return to add a payment method after your first invoice is generated."}
                  </p>
                </div>
                {hasManagePaymentMethodsPermission && invoices && invoices.length > 0 &&
                    <button className="w-full" onClick={navigateToAddPayment}>Add</button>
                }
              </>
          }
          {
              paymentMethodStatus === PaymentMethodStatus.Active && paymentMethod &&
              <>
                <div className="flex items-start gap-lg mb-lg">
                  <table className="flex-1 min-w-0">
                    <tbody>
                    <tr>
                      <td className="pr-md align-top">Type</td>
                      <th className="text-left align-top">{paymentMethod.brand}</th>
                    </tr>
                    <tr>
                      <td className="pr-md align-top">
                        {paymentMethod.brand === 'BACS' ? 'Acc last 4 Digits' : 'Card last 4 Digits'}
                      </td>
                      <th className="text-left align-top">{paymentMethod.last4Digits}</th>
                    </tr>
                    {paymentMethod.brand !== 'BACS' && (
                        <tr>
                          <td className="pr-md align-top">Expiry</td>
                          <th className="text-left align-top">
                            {`${padValue(paymentMethod.expiryMonth)}/${paymentMethod.expiryYear}`}
                          </th>
                        </tr>
                    )}
                    </tbody>
                  </table>
                  <CardBrandLogo
                    brand={paymentMethod.brand}
                    className="h-auto w-24 shrink-0 sm:w-28"
                  />
                </div>
                {hasManagePaymentMethodsPermission &&
                    <button
                      type="button"
                      onClick={navigateToAddPayment}
                      className="inline-flex items-center justify-center rounded-full bg-lime-green-alt px-lg py-sm font-bold text-pure-white shadow-sm transition-colors hover:bg-lime-green-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-lime-green-alt focus-visible:ring-offset-2"
                    >
                      Modify
                    </button>
                }
            </>
          }
        </div>
      </LoadingSpinner>
    </div>
  );
}

export default Billing;